import * as React from 'react';
import {HTMLAttributes, ReactNode} from 'react';
import style from './index.module.scss';

export type TableCellWrap = 'clamp-2' | 'break-word';

export interface IColumn<T> {
    header?: ((records: T[], column: IColumn<T>) => ReactNode) | ReactNode;
    field?: keyof T;
    hide?: boolean;
    responsive?: boolean;
    cell?: ((record: T, column: IColumn<T>) => ReactNode) | ReactNode;
    footer?: ((records: T[], column: IColumn<T>) => ReactNode) | ReactNode;
    cellAttrs?: HTMLAttributes<HTMLDivElement>;
    headerAttrs?: HTMLAttributes<HTMLDivElement>;
    footerAttrs?: HTMLAttributes<HTMLDivElement>;
    small?: boolean;
    hidden?: boolean;
    short?: boolean;
    cellWrap?: TableCellWrap;
}

export interface ITableComponentProps<T> {
    records: T[];
    columns: IColumn<T>[];
    tableAttrs?: HTMLAttributes<HTMLDivElement>;
    placeholder?: ReactNode;
    header?: boolean;
    footer?: boolean;
}

export interface ITableProps<T> extends ITableComponentProps<T> {
    records: T[];
}

export function Table<T>(props: ITableProps<T>) {
    let columns = props.columns.filter(column => !column.hide);
    let {records, placeholder, header, footer, tableAttrs = {}} = props;

    const renderIt = (object: ReactNode, args: any = null) => typeof object === 'function' ? object(...args) : object;

    let renderHeader = () => <thead className={style.tableHead}>
        <tr>
            {columns.map((column, columnIndex) => {
                let {responsive, small, hidden, short, header: columnHeader, headerAttrs = {}} = column;
                let classes = [headerAttrs.className, style.tableCell];
                if (small) {
                    classes.push(style.tableCellSmall);
                }
                if (short) {
                    classes.push(style.tableCellShort);
                }
                if (responsive) {
                    classes.push(style.tableCellResponsive);
                }
                if (!hidden) {
                    return <td key={`table-column-${columnIndex}`} {...headerAttrs}
                        className={classes.join(' ')}
                    >{(typeof columnHeader === 'function' ? columnHeader(records, column) : columnHeader) || ''}</td>;
                }
            })}
        </tr>
    </thead>;

    let renderBody = () => <tbody className={style.tableBody}>
        {placeholder && <tr key={'table-row-placeholder'}>
            <td colSpan={columns.length} className={style.tablePlaceholder}>
                {renderIt(placeholder)}
            </td>
        </tr>}
        {!placeholder && records.map((record, recordIndex) => <tr key={`table-row-${recordIndex}`}
            className={style.tableRow}
        >

            {columns.map((column, columnIndex) => {
                let {responsive, small, hidden, short, cell, cellAttrs = {}, cellWrap} = column;
                let classes = [cellAttrs.className, style.tableCell];
                if (small) {
                    classes.push(style.tableCellSmall);
                }
                if (short) {
                    classes.push(style.tableCellShort);
                }
                if (responsive) {
                    classes.push(style.tableCellResponsive);
                }
                if (!hidden) {
                    const cellValue = cell ? renderIt(cell, [record, column]) : record[column.field];
                    const wrappedValue = cellWrap
                        ? (
                            <div className={[
                                style.tableCellContent,
                                cellWrap === 'clamp-2' ? style.tableCellContentClamp2 : style.tableCellContentBreakWord,
                            ].join(' ')}>
                                {cellValue}
                            </div>
                        )
                        : cellValue;

                    return <td key={`table-cell-${recordIndex}-${columnIndex}`} {...cellAttrs} aria-label={renderIt(column.header)}
                        className={classes.join(' ')}>
                        {wrappedValue}
                    </td>;
                }
            })}</tr>)}
    </tbody>;

    const renderFooter = () => <tfoot className={style.tableFooter}>
        <tr>
            {columns.map((column, columnIndex) => {
                let {responsive, small, hidden, footer: columnFooter, footerAttrs = {}} = column;
                let classes = [footerAttrs.className, style.tableCell];
                if (small) {
                    classes.push(style.tableCellSmall);
                }
                if (responsive) {
                    classes.push(style.tableCellResponsive);
                }
                if (!hidden) {
                    return <td key={`table-footer-${columnIndex}`} {...footerAttrs}
                        className={classes.join(' ')}
                    >{columnFooter ? renderIt(columnFooter, [records, column]) : ''}</td>;
                }
            })}
        </tr>
    </tfoot>;

    return <div className={style.tableWrapper}>
        <table {...tableAttrs} className={[tableAttrs.className, style.table].join(' ')} cellPadding={0}
            cellSpacing={0}>
            {header && renderHeader()}
            {renderBody()}
            {(footer && !placeholder) && renderFooter()}
        </table>
        {!placeholder && !records.length && <div className={style.infoBlock}>No data</div>}
    </div>;
}
